<template>
  <div class="google-address-autocomplete">
    <input
      ref="addressInput"
      :class="{'is-invalid': errorField && errorMessage}"
      type="text"
      :name="name"
      class="form-control"
      :placeholder="placeholder"
      v-validate="validation"
      :autocomplete="autocomplete"
      v-model="addressInput"
      @input="onInput"
    >
    <div v-if="errorField && errorMessage" class="invalid-feedback">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  props: {
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Address'
    },
    validation: {
      type: String,
      default: 'required'
    },
    autocomplete: {
      type: String,
      default: 'address-line1'
    },
    errorField: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    initialValue: {
      type: String,
      default: ''
    },
    isInternational: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      addressInput: this.initialValue,
      autocompleteInstance: null
    }
  },
  mounted() {
    if (!this.isInternational) {
      this.initializeGooglePlaces();
    }
  },
  watch: {
    isInternational(newValue) {
      if (newValue && this.autocompleteInstance) {
        // Disable autocomplete for international addresses
        google.maps.event.clearInstanceListeners(this.$refs.addressInput);
        this.autocompleteInstance = null;
      } else if (!newValue && !this.autocompleteInstance) {
        // Enable autocomplete for US addresses
        this.initializeGooglePlaces();
      }
    }
  },
  methods: {
    initializeGooglePlaces() {
      // Create a promise to handle script loading
      if (!window.googleMapsPromise) {
        window.googleMapsPromise = new Promise((resolve, reject) => {
          if (window.google && window.google.maps && window.google.maps.places) {
            resolve();
          } else {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.MIX_GOOGLE_MAPS_API_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          }
        });
      }

      // Use the promise to ensure script is loaded before setting up autocomplete
      window.googleMapsPromise
        .then(() => this.setupAutocomplete())
        .catch(error => console.error('Error loading Google Maps:', error));
    },
    setupAutocomplete() {
      const input = this.$refs.addressInput;
      this.autocompleteInstance = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address']
      });

      this.autocompleteInstance.addListener('place_changed', () => {
        const place = this.autocompleteInstance.getPlace();
        if (!place.address_components) return;

        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zipCode = '';

        for (const component of place.address_components) {
          const type = component.types[0];
          if (type === 'street_number') {
            streetNumber = component.long_name;
          } else if (type === 'route') {
            route = component.long_name;
          } else if (type === 'locality') {
            city = component.long_name;
          } else if (type === 'administrative_area_level_1') {
            state = component.short_name;
          } else if (type === 'postal_code') {
            zipCode = component.long_name;
          }
        }

        const address1 = streetNumber + ' ' + route;
        
        this.addressInput = address1.trim();
        
        this.$emit('address-selected', {
          address_line: address1.trim(),
          city,
          state,
          zip_code: zipCode
        });
      });
    },
    onInput(event) {
      this.$emit('input', event.target.value);
    }
  }
}
</script>

<style scoped>
.google-address-autocomplete {
  position: relative;
}
</style> 